import { useEffect, useRef, useState } from "react";
import type { PdfEngine } from "../lib/engine.ts";
import {
  A4,
  geometryTransform,
  type PageInfo,
  type PageMargins,
  type PageOp,
} from "../lib/protocol.ts";
import { MARGIN_QUICK, MM_TO_PT, PAGE_PRESETS } from "../lib/ui-constants.ts";

/** Page setup sidebar — size, orientation and per-side margins with a
 *  live preview: the current page's render placed exactly where the
 *  geometry op will put it, margins shown as a hatched band. */
export function PageSetupPanel({
  engine,
  pages,
  currentPage,
  gen,
  busy,
  onApply,
  onRotate,
  onClose,
  paneWidth,
}: {
  engine: PdfEngine | null;
  pages: PageInfo[];
  currentPage: number;
  /** Baseline generation — bumps after every page op, re-renders the preview. */
  gen: number;
  busy: boolean;
  onApply: (op: Extract<PageOp, { kind: "geometry" }>) => void;
  onRotate: (target: "current" | "all", clockwise: boolean) => void;
  onClose: () => void;
  paneWidth: number;
}) {
  const cur = pages[Math.min(currentPage, pages.length - 1)] ?? A4;
  const [target, setTarget] = useState<"current" | "all">("current");
  const [preset, setPreset] = useState("keep");
  const [landscape, setLandscape] = useState(cur.width > cur.height);
  const [customW, setCustomW] = useState(Math.round(cur.width / MM_TO_PT));
  const [customH, setCustomH] = useState(Math.round(cur.height / MM_TO_PT));
  const [margins, setMargins] = useState<PageMargins>({ top: 0, right: 0, bottom: 0, left: 0 });
  const [linked, setLinked] = useState(true);
  const [fit, setFit] = useState<"scale" | "center">("scale");
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // Orientation: explicit for keep/presets; derived from the typed
  // dimensions for custom sizes (the buttons swap the inputs).
  const isLandscape = preset === "custom" ? customW > customH : landscape;
  const setOrient = (next: boolean) => {
    if (preset === "custom") {
      if (customW !== customH && customW > customH !== next) {
        const w = customW;
        setCustomW(customH);
        setCustomH(w);
      }
    } else {
      setLandscape(next);
    }
  };

  const setSide = (side: keyof PageMargins, raw: number) => {
    const v = Math.min(99, Math.max(0, raw));
    setMargins((m) =>
      linked ? { top: v, right: v, bottom: v, left: v } : { ...m, [side]: v }
    );
  };

  const dims = (): [number, number] => {
    if (preset === "custom") {
      return [Math.max(8, customW) * MM_TO_PT, Math.max(8, customH) * MM_TO_PT];
    }
    const [bw, bh] = preset === "keep" ? [cur.width, cur.height] : PAGE_PRESETS[preset]!;
    const lo = Math.min(bw, bh);
    const hi = Math.max(bw, bh);
    return landscape ? [hi, lo] : [lo, hi];
  };
  const [outW, outH] = dims();
  const marginsPt: PageMargins = {
    top: margins.top * MM_TO_PT,
    right: margins.right * MM_TO_PT,
    bottom: margins.bottom * MM_TO_PT,
    left: margins.left * MM_TO_PT,
  };
  const geomOp: Extract<PageOp, { kind: "geometry" }> = {
    kind: "geometry",
    target: target === "all" ? "all" : currentPage,
    width: outW,
    height: outH,
    margins: marginsPt,
    fit,
  };

  // Preview maths — same transform the worker will apply.
  const { s, tx, ty } = geometryTransform(cur.width, cur.height, geomOp);
  const PREV_W = 216;
  const PREV_H = 264;
  const ps = Math.min(PREV_W / outW, PREV_H / outH);
  const innerX = marginsPt.left * ps;
  const innerY = marginsPt.top * ps;
  const innerW = Math.max(8, outW - marginsPt.left - marginsPt.right) * ps;
  const innerH = Math.max(8, outH - marginsPt.top - marginsPt.bottom) * ps;
  const hasMargins =
    margins.top > 0 || margins.right > 0 || margins.bottom > 0 || margins.left > 0;

  useEffect(() => {
    if (!engine) return;
    let dead = false;
    const renderScale = (PREV_W * 2) / Math.max(cur.width, cur.height);
    void engine
      .render(currentPage, renderScale)
      .then(({ width, height, pixels }) => {
        if (dead) return;
        const c = previewCanvasRef.current;
        if (!c) return;
        c.width = width;
        c.height = height;
        c.getContext("2d")?.putImageData(
          new ImageData(new Uint8ClampedArray(pixels), width, height),
          0,
          0
        );
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, [engine, currentPage, gen, cur.width, cur.height]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fieldCls =
    "h-8 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-sm";
  const segCls = (active: boolean) =>
    `inline-flex h-8 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap px-2 text-xs ${
      active
        ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
        : "hover:bg-[color:var(--color-bg-subtle)]"
    }`;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto bg-[color:var(--color-bg)]" style={{ width: paneWidth }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onApply(geomOp);
        }}
        className="flex min-h-full flex-col gap-3 p-3"
      >
        <div className="flex items-center gap-2">
          <i className="ri-artboard-2-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
          <h2 className="text-sm font-semibold">Page setup</h2>
          <button
            type="button"
            title="Close"
            aria-label="Close page setup"
            onClick={onClose}
            className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
          >
            <i className="ri-close-line" aria-hidden />
          </button>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="w-20 shrink-0 text-[color:var(--color-fg-muted)]">Apply to</span>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value as "current" | "all")}
            className={`${fieldCls} min-w-0 flex-1`}
          >
            <option value="current">Page {currentPage + 1}</option>
            <option value="all">All pages</option>
          </select>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="w-20 shrink-0 text-[color:var(--color-fg-muted)]">Size</span>
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className={`${fieldCls} min-w-0 flex-1`}
          >
            <option value="keep">
              Keep current ({Math.round(cur.width / MM_TO_PT)} × {Math.round(cur.height / MM_TO_PT)} mm)
            </option>
            {Object.keys(PAGE_PRESETS).map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
            <option value="custom">Custom…</option>
          </select>
        </div>

        {preset === "custom" && (
          <div className="flex items-center gap-2 text-sm">
            <span className="w-20 shrink-0" />
            <span className="inline-flex min-w-0 flex-1 items-center gap-1 text-xs">
              <input
                type="number"
                min={10}
                max={2000}
                value={customW}
                onChange={(e) => setCustomW(Number(e.target.value) || 0)}
                className={`${fieldCls} w-full min-w-0 flex-1`}
              />
              ×
              <input
                type="number"
                min={10}
                max={2000}
                value={customH}
                onChange={(e) => setCustomH(Number(e.target.value) || 0)}
                className={`${fieldCls} w-full min-w-0 flex-1`}
              />
              mm
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm">
          <span className="w-20 shrink-0 text-[color:var(--color-fg-muted)]">Orientation</span>
          <div className="flex min-w-0 flex-1 overflow-hidden rounded-md border border-[color:var(--color-border)]">
            <button type="button" onClick={() => setOrient(false)} className={segCls(!isLandscape)}>
              <span className="inline-block h-3.5 w-2.5 rounded-[2px] border-[1.5px] border-current" aria-hidden />
              Portrait
            </button>
            <button type="button" onClick={() => setOrient(true)} className={segCls(isLandscape)}>
              <span className="inline-block h-2.5 w-3.5 rounded-[2px] border-[1.5px] border-current" aria-hidden />
              Landscape
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="w-20 shrink-0 text-[color:var(--color-fg-muted)]">Rotate</span>
          <div className="flex min-w-0 flex-1 gap-1">
            <button
              type="button"
              disabled={busy}
              title={`Rotate ${target === "all" ? "every page" : `page ${currentPage + 1}`} anti-clockwise`}
              onClick={() => onRotate(target, false)}
              className="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-md border border-[color:var(--color-border)] text-xs hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-anticlockwise-line" aria-hidden /> 90° left
            </button>
            <button
              type="button"
              disabled={busy}
              title={`Rotate ${target === "all" ? "every page" : `page ${currentPage + 1}`} clockwise`}
              onClick={() => onRotate(target, true)}
              className="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-md border border-[color:var(--color-border)] text-xs hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-clockwise-line" aria-hidden /> 90° right
            </button>
          </div>
        </div>

        <div className="flex items-start gap-2 text-sm">
          <span className="w-20 shrink-0 pt-1.5 text-[color:var(--color-fg-muted)]">
            Margins <span className="text-[10px]">mm</span>
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex gap-1">
              {MARGIN_QUICK.map(([label, v]) => {
                const active =
                  margins.top === v && margins.right === v && margins.bottom === v && margins.left === v;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setMargins({ top: v, right: v, bottom: v, left: v })}
                    className={`h-6 flex-1 rounded border text-[11px] ${
                      active
                        ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                        : "border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-subtle)]"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {(["top", "bottom", "left", "right"] as const).map((side) => (
                <label key={side} className="flex items-center gap-1 text-[11px]">
                  <span className="w-9 capitalize text-[color:var(--color-fg-muted)]">{side}</span>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={margins[side]}
                    onChange={(e) => setSide(side, Number(e.target.value) || 0)}
                    className={`${fieldCls} h-7 w-full min-w-0 flex-1`}
                  />
                </label>
              ))}
            </div>
            <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-[color:var(--color-fg-muted)]">
              <input type="checkbox" checked={linked} onChange={(e) => setLinked(e.target.checked)} />
              Same on all sides
            </label>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="w-20 shrink-0 text-[color:var(--color-fg-muted)]">Content</span>
          <div className="flex min-w-0 flex-1 flex-wrap gap-x-3 gap-y-1 text-xs">
            <label className="inline-flex cursor-pointer items-center gap-1">
              <input type="radio" checked={fit === "scale"} onChange={() => setFit("scale")} />
              Scale to fit
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1">
              <input type="radio" checked={fit === "center"} onChange={() => setFit("center")} />
              Keep size, centre
            </label>
          </div>
        </div>

        {/* live preview: hatched band = margins, dashed box = content area */}
        <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)] p-3">
          <div className="flex items-center justify-center" style={{ minHeight: 120 }}>
            <div
              className="relative overflow-hidden shadow-md"
              style={{
                width: Math.round(outW * ps),
                height: Math.round(outH * ps),
                background:
                  "repeating-linear-gradient(45deg, rgb(80 110 220 / 0.16) 0 4px, rgb(80 110 220 / 0.05) 4px 8px) #fff",
              }}
            >
              <div
                className="absolute bg-white"
                style={{ left: innerX, top: innerY, width: innerW, height: innerH }}
              />
              <canvas
                ref={previewCanvasRef}
                className="absolute"
                style={{
                  left: tx * ps,
                  top: ty * ps,
                  width: s * cur.width * ps,
                  height: s * cur.height * ps,
                }}
              />
              {hasMargins && (
                <div
                  className="pointer-events-none absolute border-[1.5px] border-dashed border-[color:var(--color-accent)]"
                  style={{ left: innerX, top: innerY, width: innerW, height: innerH }}
                />
              )}
            </div>
          </div>
          <p className="mt-2 text-center text-[11px] text-[color:var(--color-fg-muted)]">
            {Math.round(outW / MM_TO_PT)} × {Math.round(outH / MM_TO_PT)} mm{" "}
            {outW === outH ? "" : isLandscape ? "landscape" : "portrait"}
            {hasMargins
              ? ` · margins ${margins.top}/${margins.right}/${margins.bottom}/${margins.left} mm`
              : " · no margins"}
            <br />
            Previewing page {currentPage + 1}
            {target === "all" ? " — applies to all pages" : ""}. Undoable.
          </p>
        </div>

        <div className="mt-auto flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      </form>
    </div>
  );
}
